import { Button, Stack } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import { Maybe } from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  FullscreenModal,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'

import {
  ItemSelectBox,
  ItemSelectBoxProps,
  ItemSelectBoxSkeleton,
} from './ItemSelectBox'
import { ItemSelectForm, ItemSelectFormProps } from './ItemSelectForm'

export type ItemSelectModalProps = {
  artistId: string
  open: boolean
  onClose: () => void
  loading: boolean
  hasNextPage: boolean
  value: string
  onFetchMore: () => Promise<void>
  items: Maybe<{ edges: { node: ItemSelectBoxProps['item'] }[] }>
  onSearch: ItemSelectFormProps['onSubmit']
  onSelect: (id: string) => void
}

export const ItemSelectModal: Organism<ItemSelectModalProps> = ({
  artistId,
  open,
  value,
  onClose,
  loading,
  hasNextPage,
  onFetchMore,
  items,
  onSearch,
  onSelect,
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  const [itemSelected, setItemSelected] = useState<string>('')

  useEffect(() => {
    setItemSelected(value)
  }, [value])

  const handleSelect = useCallback((itemId: string) => {
    setItemSelected(prevState => (prevState === itemId ? '' : itemId))
  }, [])

  return (
    <FullscreenModal open={open}>
      <ArtistThemeProvider artistId={artistId}>
        <ConsoleBaseLayout
          customNavigation={
            itemSelected ? (
              <FixedBottomCell onSelect={() => onSelect(itemSelected)} />
            ) : null
          }
          margin
          header={{
            left: (
              <IconButton.Back
                edge="start"
                size="medium"
                color="white"
                sx={{
                  color: 'white',
                  '.MuiSvgIcon-root': {
                    color: 'inherit',
                  },
                }}
                onClick={() => {
                  onClose()
                  setItemSelected(value)
                }}
              />
            ),
            title: t('itemSelection'),
          }}
        >
          <ItemSelectForm onSubmit={onSearch} />
          <ScrollFetcher
            nodes={items?.edges.map(({ node }) => (
              <ItemSelectBox
                item={node}
                checked={itemSelected === node.id}
                key={node.id}
                onSelect={handleSelect}
              />
            ))}
            emptyMessage={t('emptyItemSelect')}
            loading={loading}
            hasNextPage={hasNextPage}
            onFetchMore={onFetchMore}
            loadingComponent={
              <Stack spacing={5} sx={{ pb: 2 }}>
                {Array.from(Array(4).keys()).map(key => (
                  <ItemSelectBoxSkeleton key={key} />
                ))}
              </Stack>
            }
          />
        </ConsoleBaseLayout>
      </ArtistThemeProvider>
    </FullscreenModal>
  )
}

type FixedBottomCellProps = {
  onSelect: () => void
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({ onSelect }) => {
  const { t } = useTranslation('consoleMissionCreate')
  return (
    <CellFrame sx={{ py: 4, bgcolor: 'grey.50', textAlign: 'center' }}>
      <Button size="large" sx={{ width: 244 }} onClick={onSelect}>
        {t('addSelectedItem')}
      </Button>
    </CellFrame>
  )
}
